import type {
  TileContent,
  TileType,
} from '@features/instructors/types/tile.types';

import { InstructorsPendingLetterProvider } from '@features/instructors/contexts/pending-letter/InstructorsPendingLetterProvider';
import { useInstructorsPendingLetterData } from '@features/instructors/contexts/pending-letter/useInstructorsPendingLetterData';
import { useInstructorsProcessingRequests } from '@features/instructors/contexts/processing-requests/useInstructorsProcessingRequests';
import { useInstructorsUnapprovedData } from '@features/instructors/contexts/unapproved/useInstructorsUnapprovedData';
import { useInstructorNavigation } from '@features/instructors/hooks/use-instructor-navigation/useInstructorNavigation';
import { TilesCarousel } from '@shared/components/tiles-carousel/TilesCarousel';
import * as React from 'react';

import { InstructorActionTile } from './instructor-action-tile/InstructorActionTile';
import styles from './InstructorActionTiles.module.css';
import {
  buildPendingLetterTileContent,
  buildProcessingRequestsTileContent,
  buildUnapprovedTileContent,
} from './InstructorActionTiles.utils';

const InstructorActionTilesContent = React.memo(
  (): React.ReactElement | null => {
    const {
      data: unapprovedData,
      isError: unapprovedIsError,
      isLoading: unapprovedIsLoading,
      isFetching: unapprovedIsFetching,
    } = useInstructorsUnapprovedData();
    const {
      data: pendingLetterData,
      isError: pendingLetterIsError,
      isLoading: pendingLetterIsLoading,
    } = useInstructorsPendingLetterData();
    const { isProcessing, processingCount } =
      useInstructorsProcessingRequests();

    const visibleTiles: TileContent[] = React.useMemo(
      () =>
        [
          buildUnapprovedTileContent(
            unapprovedData,
            unapprovedIsError,
            unapprovedIsLoading || unapprovedIsFetching
          ),
          buildPendingLetterTileContent(
            pendingLetterData,
            pendingLetterIsError,
            pendingLetterIsLoading
          ),
          buildProcessingRequestsTileContent(isProcessing, processingCount),
        ].filter((t): t is TileContent => t !== null),
      [
        unapprovedData,
        unapprovedIsError,
        unapprovedIsLoading,
        unapprovedIsFetching,
        pendingLetterData,
        pendingLetterIsError,
        pendingLetterIsLoading,
        isProcessing,
        processingCount,
      ]
    );

    const { navigateToManageInstructorsPanel, navigateToApprovalLetterPanel } =
      useInstructorNavigation();

    const handleInstructorsToApproveClick = React.useCallback(() => {
      if (!isProcessing) {
        navigateToManageInstructorsPanel();
      }
    }, [isProcessing, navigateToManageInstructorsPanel]);

    const handleApprovalLettersClick = React.useCallback(
      () => navigateToApprovalLetterPanel(),
      [navigateToApprovalLetterPanel]
    );

    const tileClickHandlers: Record<TileType, () => void> = React.useMemo(
      () => ({
        'instructors-to-approve': handleInstructorsToApproveClick,
        'approval-letters': handleApprovalLettersClick,
        /* v8 ignore next */
        'processing-requests': () => undefined,
      }),
      [handleInstructorsToApproveClick, handleApprovalLettersClick]
    );

    if (visibleTiles.length === 0) return null;

    return (
      <section
        data-testid="instructors-actions-tiles"
        className={styles.instructorActionTiles}
      >
        <h2 className={`heading-01 ${styles.tilesHeading}`}>
          Needs your attention
        </h2>
        <TilesCarousel
          scrollContainerClassName={styles.instructorActionTilesCarousel}
        >
          {visibleTiles.map(tile => (
            <InstructorActionTile
              key={tile.type}
              type={tile.type}
              count={tile.count}
              description={tile.description}
              showInfoIcon={tile.showInfoIcon}
              showProcessingLoader={tile.showProcessingLoader}
              onClick={tileClickHandlers[tile.type]}
              isLoading={tile.isLoading}
            />
          ))}
        </TilesCarousel>
      </section>
    );
  }
);

InstructorActionTilesContent.displayName = 'InstructorActionTilesContent';

const InstructorActionTiles = React.memo(
  (): React.ReactElement => (
    <InstructorsPendingLetterProvider>
      <InstructorActionTilesContent />
    </InstructorsPendingLetterProvider>
  )
);

InstructorActionTiles.displayName = 'InstructorActionTiles';
export { InstructorActionTiles };
